// CompanyCenter.jsx

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API_BASE_URL = 'https://kigaawater.inkenya.co.ke/api';
const SERVER_URL = 'https://kigaawater.inkenya.co.ke';

const galleryStyles = {
    imageCard: { margin: '10px', padding: '10px', border: '1px solid #ddd', borderRadius: '4px' },
    imagePreview: { width: '100px', height: '100px', objectFit: 'cover' },
    orderButton: { margin: '0 5px', padding: '5px 10px', backgroundColor: '#007bff', color: 'white', border: 'none', cursor: 'pointer' }
};

const styles = {
    container: { maxWidth: '1000px', margin: '20px auto', padding: '20px', backgroundColor: '#fff', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)' },
    header: { color: '#007bff', borderBottom: '2px solid #eee', paddingBottom: '10px', marginBottom: '20px' },
    section: { marginBottom: '30px', padding: '15px', border: '1px solid #ddd', borderRadius: '6px', backgroundColor: '#f9f9f9' },
    sectionHeader: { color: '#333', borderBottom: '1px solid #eee', paddingBottom: '10px', marginBottom: '15px' },
    table: { width: '100%', borderCollapse: 'collapse', marginBottom: '20px', backgroundColor: '#fff' },
    th: { padding: '12px 15px', border: '1px solid #ddd', textAlign: 'left', backgroundColor: '#f1f1f1' },
    td: { padding: '12px 15px', border: '1px solid #eee', verticalAlign: 'middle' },
    input: { padding: '8px', border: '1px solid #ccc', borderRadius: '4px', width: '100%', boxSizing: 'border-box' },
    button: { padding: '10px 15px', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold', margin: '0 5px', transition: 'background-color 0.3s' },
    success: { backgroundColor: '#28a745', color: 'white' },
    error: { backgroundColor: '#dc3545', color: 'white' },
    primary: { backgroundColor: '#007bff', color: 'white' },
    secondary: { backgroundColor: '#6c757d', color: 'white' },
    status: { padding: '10px', marginTop: '15px', borderRadius: '4px', fontWeight: 'bold' }
};

const getTodayDate = () => new Date().toISOString().split('T')[0];

const readBlobError = (blob) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
            try {
                // Attempt to parse the blob content as JSON
                const errorData = JSON.parse(reader.result);
                // Assume the backend sends an error object like { "error": "Message" }
                resolve(errorData.error || 'Unknown server error.');
            } catch (e) {
                // If parsing fails (e.g., the server sent an HTML page or malformed JSON)
                reject('Server returned an unexpected error format.');
            }
        };
        reader.onerror = () => reject('Error reading response blob.');
        reader.readAsText(blob);
    });
};

// --------------------------------------------------
// BLOCKS MANAGER
// --------------------------------------------------
const BlocksManager = ({ reportStatusSetter }) => {
    const [blocks, setBlocks] = useState([]);
    const [newBlock, setNewBlock] = useState({ block_name: '', connection_fee: 0 });
    const [editingId, setEditingId] = useState(null);
    const [editData, setEditData] = useState({});

    const fetchBlocks = useCallback(async () => {
        try {
            const response = await axios.get(`${API_BASE_URL}/company-center/blocks`);
            setBlocks(response.data);
        } catch (error) {
            reportStatusSetter({ message: 'Failed to fetch blocks.', type: 'error' });
        }
    }, [reportStatusSetter]);

    useEffect(() => { fetchBlocks(); }, [fetchBlocks]);

    const handleCreate = async () => {
        if (!newBlock.block_name || isNaN(newBlock.connection_fee)) return;
        try {
            await axios.post(`${API_BASE_URL}/company-center/blocks`, newBlock);
            fetchBlocks();
            setNewBlock({ block_name: '', connection_fee: 0 });
            reportStatusSetter({ message: 'Block created successfully!', type: 'success' });
        } catch (error) {
            reportStatusSetter({ message: error.response?.data?.error || 'Failed to create block.', type: 'error' });
        }
    };

    const handleUpdate = async (id) => {
        try {
            await axios.put(`${API_BASE_URL}/company-center/blocks/${id}`, editData);
            setEditingId(null);
            fetchBlocks();
            reportStatusSetter({ message: 'Block updated successfully!', type: 'success' });
        } catch (error) {
            reportStatusSetter({ message: error.response?.data?.error || 'Failed to update block.', type: 'error' });
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Delete this block?')) return;
        try {
            await axios.delete(`${API_BASE_URL}/company-center/blocks/${id}`);
            fetchBlocks();
            reportStatusSetter({ message: 'Block deleted successfully!', type: 'success' });
        } catch (error) {
            reportStatusSetter({ message: error.response?.data?.error || 'Failed to delete block.', type: 'error' });
        }
    };

    const startEdit = (block) => {
        setEditingId(block.block_id);
        setEditData({ block_name: block.block_name, connection_fee: block.connection_fee });
    };

    return (
        <div style={styles.section}>
            <h3 style={styles.sectionHeader}>3. Manage Blocks & Connection Fees</h3>

            <table style={styles.table}>
                <thead>
                    <tr>
                        <th style={styles.th}>Block Name</th>
                        <th style={styles.th}>Connection Fee (Ksh)</th>
                        <th style={styles.th}>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {blocks.map(block => (
                        <tr key={block.block_id}>
                            <td style={styles.td}>
                                {editingId === block.block_id ? (
                                    <input style={styles.input} value={editData.block_name}
                                        onChange={e => setEditData({ ...editData, block_name: e.target.value })} />
                                ) : block.block_name}
                            </td>
                            <td style={styles.td}>
                                {editingId === block.block_id ? (
                                    <input style={styles.input} type="number" value={editData.connection_fee}
                                        onChange={e => setEditData({ ...editData, connection_fee: parseFloat(e.target.value) || 0 })} />
                                ) : parseFloat(block.connection_fee).toFixed(2)}
                            </td>
                            <td style={styles.td}>
                                {editingId === block.block_id ? (
                                    <>
                                        <button style={{ ...styles.button, ...styles.success }} onClick={() => handleUpdate(block.block_id)}>Save</button>
                                        <button style={{ ...styles.button, ...styles.secondary }} onClick={() => setEditingId(null)}>Cancel</button>
                                    </>
                                ) : (
                                    <>
                                        <button style={{ ...styles.button, ...styles.primary }} onClick={() => startEdit(block)}>Edit</button>
                                        <button style={{ ...styles.button, ...styles.error }} onClick={() => handleDelete(block.block_id)}>Delete</button>
                                    </>
                                )}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>

            <h4 style={{ color: '#333', marginTop: '20px' }}>Add New Block</h4>
            <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                <input style={styles.input} placeholder="Block Name" value={newBlock.block_name}
                    onChange={e => setNewBlock({ ...newBlock, block_name: e.target.value })} />
                <input style={styles.input} type="number" placeholder="Connection Fee (Ksh)" value={newBlock.connection_fee}
                    onChange={e => setNewBlock({ ...newBlock, connection_fee: parseFloat(e.target.value) || 0 })} />
                <button style={{ ...styles.button, ...styles.primary }} onClick={handleCreate}>Add Block</button>
            </div>
        </div>
    );
};

// --------------------------------------------------
// EMPLOYEES MANAGER – FINAL VERSION (All Bugs Fixed!)
// --------------------------------------------------
const ROLE_LIMITS = {
    SuperAdmin: 1,
    Admin: 3,
    Secretary: 1,
    'Field Agent': 20
};

// Map role name → role_id (matches your DB)
const ROLE_NAME_TO_ID = {
    SuperAdmin: 1,
    Admin: 2,
    Secretary: 3,
    'Field Agent': 4
};

const EmployeesManager = ({ reportStatusSetter }) => {
    const [employees, setEmployees] = useState([]);
    const [blocks, setBlocks] = useState([]);
    const [newEmployee, setNewEmployee] = useState({
        first_name: '',
        last_name: '',
        phone_number: '',
        role: '',
        password: '',
        assignedBlocks: []
    });
    const [editingId, setEditingId] = useState(null);
    const [editData, setEditData] = useState({
        first_name: '',
        last_name: '',
        phone_number: '',
        role: '',
        assignedBlocks: []
    });
    const [roleCounts, setRoleCounts] = useState({
        SuperAdmin: 0,
        Admin: 0,
        Secretary: 0,
        'Field Agent': 0
    });

    const fetchEmployees = useCallback(async () => {
        try {
            const response = await axios.get(`${API_BASE_URL}/company-center/employees`);
            setEmployees(response.data.employees || []);
        } catch (error) {
            reportStatusSetter({ message: 'Failed to fetch employees.', type: 'error' });
        }
    }, [reportStatusSetter]);

    const fetchBlocks = useCallback(async () => {
        try {
            const response = await axios.get(`${API_BASE_URL}/company-center/blocks`);
            setBlocks(response.data);
        } catch (error) {
            reportStatusSetter({ message: 'Failed to fetch blocks.', type: 'error' });
        }
    }, [reportStatusSetter]);

    useEffect(() => {
        fetchEmployees();
        fetchBlocks();
    }, [fetchEmployees, fetchBlocks]);

    useEffect(() => {
        const counts = { SuperAdmin: 0, Admin: 0, Secretary: 0, 'Field Agent': 0 };
        employees.forEach(emp => {
            const roleName = emp.role_name || emp.role;
            if (roleName && counts.hasOwnProperty(roleName)) {
                counts[roleName]++;
            }
        });
        setRoleCounts(counts);
    }, [employees]);

    // CREATE NEW EMPLOYEE
    const handleCreate = async () => {
        if (!newEmployee.first_name || !newEmployee.last_name || !newEmployee.phone_number || !newEmployee.role || !newEmployee.password) {
            reportStatusSetter({ message: 'All fields are required (including password).', type: 'error' });
            return;
        }
        if (roleCounts[newEmployee.role] >= ROLE_LIMITS[newEmployee.role]) {
            reportStatusSetter({ message: `Maximum ${newEmployee.role}(s) reached.`, type: 'error' });
            return;
        }
        if (newEmployee.role === 'Field Agent' && newEmployee.assignedBlocks.length === 0) {
            reportStatusSetter({ message: 'Field Agents must be assigned at least one block.', type: 'error' });
            return;
        }
        if (newEmployee.assignedBlocks.length > 4) {
            reportStatusSetter({ message: 'Maximum 4 blocks per Field Agent.', type: 'error' });
            return;
        }

        try {
            await axios.post(`${API_BASE_URL}/company-center/employees`, {
                first_name: newEmployee.first_name,
                last_name: newEmployee.last_name,
                phone_number: newEmployee.phone_number,
                role_id: ROLE_NAME_TO_ID[newEmployee.role],   // ← Correct: send integer
                password: newEmployee.password,
                assigned_blocks: newEmployee.assignedBlocks,
		is_active: true
            });
            fetchEmployees();
            setNewEmployee({ first_name: '', last_name: '', phone_number: '', role: '', password: '', assignedBlocks: [] });
            reportStatusSetter({ message: 'Employee created successfully!', type: 'success' });
        } catch (error) {
            reportStatusSetter({ message: error.response?.data?.error || 'Failed to create employee.', type: 'error' });
        }
    };

    // UPDATE EXISTING EMPLOYEE
    const handleUpdate = async (employee_id) => {
        const currentEmployee = employees.find(e => e.employee_id === employee_id);

        if (!editData.first_name || !editData.last_name || !editData.phone_number || !editData.role) {
            reportStatusSetter({ message: 'All fields are required.', type: 'error' });
            return;
        }

        // Role change limit check
        if (editData.role !== (currentEmployee.role_name || currentEmployee.role)) {
            const newCount = roleCounts[editData.role] + 1;
            if (newCount > ROLE_LIMITS[editData.role]) {
                reportStatusSetter({ message: `Cannot assign ${editData.role} — limit reached.`, type: 'error' });
                return;
            }
        }

        if (editData.role === 'Field Agent') {
            if (!editData.assignedBlocks || editData.assignedBlocks.length === 0) {
                reportStatusSetter({ message: 'Field Agent must have at least one block.', type: 'error' });
                return;
            }
            if (editData.assignedBlocks.length > 4) {
                reportStatusSetter({ message: 'Maximum 4 blocks allowed.', type: 'error' });
                return;
            }
        }

        try {
            await axios.put(`${API_BASE_URL}/company-center/employees/${employee_id}`, {
                first_name: editData.first_name,
                last_name: editData.last_name,
                phone_number: editData.phone_number,
                role_id: ROLE_NAME_TO_ID[editData.role],       // ← Correct: send integer
                assigned_blocks: editData.role === 'Field Agent' ? editData.assignedBlocks : [],
   		is_active: true         
});
            setEditingId(null);
            fetchEmployees();
            reportStatusSetter({ message: 'Employee updated successfully!', type: 'success' });
        } catch (error) {
            reportStatusSetter({ message: error.response?.data?.error || 'Failed to update employee.', type: 'error' });
        }
    };

    const startEdit = (emp) => {
        setEditingId(emp.employee_id);
        setEditData({
            first_name: emp.first_name || '',
            last_name: emp.last_name || '',
            phone_number: emp.phone_number || '',
            role: emp.role_name || emp.role || '',
            assignedBlocks: Array.isArray(emp.assigned_blocks) ? emp.assigned_blocks : []
        });
    };

    const handleDelete = async (employee_id) => {
        if (!window.confirm('Delete this employee?')) return;
        try {
            await axios.delete(`${API_BASE_URL}/company-center/employees/${employee_id}`);
            fetchEmployees();
            reportStatusSetter({ message: 'Employee deleted successfully!', type: 'success' });
        } catch (error) {
            reportStatusSetter({ message: error.response?.data?.error || 'Failed to delete employee.', type: 'error' });
        }
    };

    return (
        <div style={styles.section}>
            <h3 style={styles.sectionHeader}>2. Manage Company Employees</h3>
            <table style={styles.table}>
                <thead>
                    <tr>
                        <th style={styles.th}>Name</th>
                        <th style={styles.th}>Phone Number</th>
                        <th style={styles.th}>Role</th>
                        <th style={styles.th}>Assigned Blocks</th>
                        <th style={styles.th}>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {employees.map(emp => (
                        <tr key={emp.employee_id}>
                            <td style={styles.td}>
                                {editingId === emp.employee_id ? (
                                    <div style={{ display: 'flex', gap: '8px' }}>
                                        <input style={{ ...styles.input, width: '48%' }} value={editData.first_name} onChange={e => setEditData({ ...editData, first_name: e.target.value })} />
                                        <input style={{ ...styles.input, width: '48%' }} value={editData.last_name} onChange={e => setEditData({ ...editData, last_name: e.target.value })} />
                                    </div>
                                ) : `${emp.first_name} ${emp.last_name || ''}`}
                            </td>
                            <td style={styles.td}>
                                {editingId === emp.employee_id ? (
                                    <input style={styles.input} value={editData.phone_number} onChange={e => setEditData({ ...editData, phone_number: e.target.value })} />
                                ) : emp.phone_number}
                            </td>
                            <td style={styles.td}>
                                {editingId === emp.employee_id ? (
                                    <select value={editData.role} onChange={e => setEditData({
                                        ...editData,
                                        role: e.target.value,
                                        assignedBlocks: e.target.value === 'Field Agent' ? editData.assignedBlocks : []
                                    })}>
                                        <option value="">Select Role</option>
                                        <option value="SuperAdmin">SuperAdmin</option>
                                        <option value="Admin">Admin</option>
                                        <option value="Secretary">Secretary</option>
                                        <option value="Field Agent">Field Agent</option>
                                    </select>
                                ) : (emp.role_name || emp.role)}
                            </td>
                            <td style={styles.td}>
                                {editingId === emp.employee_id ? (
                                    <select multiple size="5" value={editData.assignedBlocks || []}
                                        onChange={(e) => {
                                            const selected = Array.from(e.target.selectedOptions, o => parseInt(o.value));
                                            if (selected.length > 4) {
                                                reportStatusSetter({ message: 'Max 4 blocks.', type: 'error' });
                                                return;
                                            }
                                            setEditData({ ...editData, assignedBlocks: selected });
                                        }}
                                        disabled={editData.role !== 'Field Agent'}>
                                        {blocks.map(block => (
                                            <option key={block.block_id} value={block.block_id}>{block.block_name}</option>
                                        ))}
                                    </select>
                                ) : (
                                    emp.role_name === 'Field Agent' || emp.role === 'Field Agent'
                                        ? (emp.assigned_blocks?.length > 0
                                            ? emp.assigned_blocks.map(id => blocks.find(b => b.block_id === id)?.block_name || id).filter(Boolean).join(', ')
                                            : '-')
                                        : '-'
                                )}
                            </td>
                            <td style={styles.td}>
                                {editingId === emp.employee_id ? (
                                    <>
                                        <button style={{ ...styles.button, ...styles.success }} onClick={() => handleUpdate(emp.employee_id)}>Save</button>
                                        <button style={{ ...styles.button, ...styles.secondary }} onClick={() => setEditingId(null)}>Cancel</button>
                                    </>
                                ) : (
                                    <>
                                        <button style={{ ...styles.button, ...styles.primary }} onClick={() => startEdit(emp)}>Edit</button>
                                        <button style={{ ...styles.button, ...styles.error }} onClick={() => handleDelete(emp.employee_id)}>Delete</button>
                                    </>
                                )}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>

            {/* ADD NEW EMPLOYEE FORM */}
            <h4 style={{ color: '#333', marginTop: '20px' }}>Add New Employee</h4>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginTop: '10px' }}>
                <div><label>First Name</label><input style={styles.input} value={newEmployee.first_name} onChange={e => setNewEmployee({ ...newEmployee, first_name: e.target.value })} /></div>
                <div><label>Last Name</label><input style={styles.input} value={newEmployee.last_name} onChange={e => setNewEmployee({ ...newEmployee, last_name: e.target.value })} /></div>
                <div><label>Phone Number</label><input style={styles.input} value={newEmployee.phone_number} onChange={e => setNewEmployee({ ...newEmployee, phone_number: e.target.value })} /></div>
                <div>
                    <label>Role</label>
                    <select value={newEmployee.role} onChange={e => setNewEmployee({ ...newEmployee, role: e.target.value, assignedBlocks: [] })}>
                        <option value="">Select Role</option>
                        <option value="SuperAdmin" disabled={roleCounts.SuperAdmin >= 1}>SuperAdmin ({roleCounts.SuperAdmin}/1)</option>
                        <option value="Admin" disabled={roleCounts.Admin >= 3}>Admin ({roleCounts.Admin}/3)</option>
                        <option value="Secretary" disabled={roleCounts.Secretary >= 1}>Secretary ({roleCounts.Secretary}/1)</option>
                        <option value="Field Agent">Field Agent ({roleCounts['Field Agent']}/20)</option>
                    </select>
                </div>
                <div><label>Initial Password</label><input style={styles.input} type="password" value={newEmployee.password} onChange={e => setNewEmployee({ ...newEmployee, password: e.target.value })} /></div>
                {newEmployee.role === 'Field Agent' && (
                    <div style={{ gridColumn: '1 / span 2' }}>
                        <label>Assigned Blocks (max 4)</label>
                        <select multiple size="6" value={newEmployee.assignedBlocks} onChange={e => {
                            const selected = Array.from(e.target.selectedOptions, o => parseInt(o.value));
                            if (selected.length > 4) {
                                reportStatusSetter({ message: 'Maximum 4 blocks allowed.', type: 'error' });
                                return;
                            }
                            setNewEmployee({ ...newEmployee, assignedBlocks: selected });
                        }}>
                            {blocks.map(block => (
                                <option key={block.block_id} value={block.block_id}>{block.block_name}</option>
                            ))}
                        </select>
                    </div>
                )}
            </div>
            <button style={{ ...styles.button, ...styles.primary, marginTop: '15px' }} onClick={handleCreate}>
                Add Employee
            </button>
        </div>
    );
};




// --------------------------------------------------
// GALLERY MANAGER (upload & ordering)
// --------------------------------------------------
const GalleryManager = ({ reportStatusSetter }) => {
    const [images, setImages] = useState([]);
    const [file, setFile] = useState(null);

    const fetchImages = useCallback(async () => {
        try {
            const response = await axios.get(`${API_BASE_URL}/gallery`);
            setImages(response.data);
        } catch (error) {
            reportStatusSetter({ message: 'Failed to fetch gallery images.', type: 'error' });
        }
    }, [reportStatusSetter]);

    useEffect(() => { fetchImages(); }, [fetchImages]);

   const handleUpload = async () => {
    if (!file) return;

    const formData = new FormData();
    formData.append('image', file);  // ← CHANGE 'file' → 'image'

    try {
        await axios.post(
            `${API_BASE_URL}/gallery`,  // ← CHANGE /uploads/gallery → /gallery
            formData,
            {
                headers: { 'Content-Type': 'multipart/form-data' },
                withCredentials: true  // ← If using authenticateToken (cookies)
            }
        );

        fetchImages();
        setFile(null);
        reportStatusSetter({ message: 'Image uploaded successfully!', type: 'success' });
    } catch (error) {
        console.error('Upload error:', error.response?.data);
        reportStatusSetter({ 
            message: error.response?.data?.error || 'Failed to upload image.', 
            type: 'error' 
        });
    }
};

    const handleDelete = async (id) => {
        if (!window.confirm('Delete this image?')) return;
        try {
            await axios.delete(`${API_BASE_URL}/gallery/${id}`);
            fetchImages();
            reportStatusSetter({ message: 'Image deleted successfully!', type: 'success' });
        } catch (error) {
            reportStatusSetter({ message: error.response?.data?.error || 'Failed to delete image.', type: 'error' });
        }
    };

    const handleMove = async (id, direction) => {
        try {
            await axios.put(`${API_BASE_URL}/gallery/${id}/order`, { direction });
            fetchImages();
            reportStatusSetter({ message: 'Image order updated!', type: 'success' });
        } catch (error) {
            reportStatusSetter({ message: error.response?.data?.error || 'Failed to update order.', type: 'error' });
        }
    };

    return (
        <div style={styles.section}>
            <h3 style={styles.sectionHeader}>4. Manage Gallery Images</h3>
            <p>Upload and arrange images for the gallery page.</p>

            <input type="file" accept="image/*" onChange={e => setFile(e.target.files[0])} />
            <button style={{ ...styles.button, ...styles.primary }} onClick={handleUpload}>Upload Image</button>

            <div style={{ marginTop: '20px' }}>
                {images.map((img, idx) => (
                    <div key={img.id} style={galleryStyles.imageCard}>
                        <img 
    src={`${SERVER_URL}${img.image_url}`} 
    alt={img.filename} 
    style={galleryStyles.imagePreview} 
/>
                        <p>{img.filename}</p>
                        <button style={galleryStyles.orderButton} onClick={() => handleMove(img.id, 'up')} disabled={idx === 0}>Up</button>
                        <button style={galleryStyles.orderButton} onClick={() => handleMove(img.id, 'down')} disabled={idx === images.length - 1}>Down</button>
                        <button style={{ ...styles.button, ...styles.error }} onClick={() => handleDelete(img.id)}>Delete</button>
                    </div>
                ))}
            </div>
        </div>
    );
};

// --------------------------------------------------
// SETTINGS MANAGER
// --------------------------------------------------

// Define months array for the financial year dropdown
const MONTHS = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' },
];

const SettingsManager = ({ reportStatusSetter }) => {
    const [settings, setSettings] = useState({
        company_name: '',
        financial_year_start_month: 1, // Default to January
        address: '',
        phone_number: '',
    });
    const [loading, setLoading] = useState(true);

    const fetchSettings = useCallback(async () => {
        setLoading(true);
        try {
            // GET: /api/company-center/settings
            const response = await axios.get(`${API_BASE_URL}/company-center/settings`);
            
            setSettings({
                ...response.data,
                // Ensure the month is converted to an integer
                financial_year_start_month: parseInt(response.data.financial_year_start_month, 10) || 1,
            });
            reportStatusSetter(null);
        } catch (error) {
            // Handle 404: Assume settings don't exist yet (first time running)
            if (error.response?.status !== 404) {
                reportStatusSetter({ message: 'Failed to load company settings.', type: 'error' });
                console.error('Error fetching company settings:', error);
            } else {
                // Keep default state and allow user to save (which triggers INSERT on the backend)
                setSettings(prev => ({ ...prev, financial_year_start_month: 1 }));
            }
        } finally {
            setLoading(false);
        }
    }, [reportStatusSetter]);

    useEffect(() => {
        fetchSettings();
    }, [fetchSettings]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setSettings(prev => ({
            ...prev,
            // Convert month value to integer immediately
            [name]: name === 'financial_year_start_month' ? parseInt(value, 10) : value
        }));
    };

    const handleSave = async () => {
        try {
            // PUT: /api/company-center/settings
            await axios.put(`${API_BASE_URL}/company-center/settings`, settings);
            reportStatusSetter({ message: 'Company settings saved successfully!', type: 'success' });
            fetchSettings(); // Re-fetch to ensure state is synchronized
        } catch (error) {
            reportStatusSetter({ 
                message: error.response?.data?.error || 'Failed to update company settings.', 
                type: 'error' 
            });
            console.error('Error saving company settings:', error);
        }
    };

    if (loading) {
        return (
            <div style={styles.section}>
                <h3 style={styles.sectionHeader}>4. Company Settings</h3>
                <p>Loading settings...</p>
            </div>
        );
    }

    return (
        <div style={styles.section}>
            <h3 style={styles.sectionHeader}>1. Company Settings</h3>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                <div>
                    <label>Company Name</label>
                    <input 
                        style={styles.input} 
                        type="text" 
                        name="company_name"
                        value={settings.company_name}
                        onChange={handleInputChange}
                        placeholder="e.g., Kigaa Water Society"
                    />
                </div>
                <div>
                    <label>Phone Number</label>
                    <input 
                        style={styles.input} 
                        type="text" 
                        name="phone_number"
                        value={settings.phone_number}
                        onChange={handleInputChange}
                        placeholder="+254 7XX XXX XXX"
                    />
                </div>
                <div>
                    <label>Financial Year Start Month</label>
                    <select 
                        style={styles.input} 
                        name="financial_year_start_month"
                        value={settings.financial_year_start_month}
                        onChange={handleInputChange}
                    >
                        {MONTHS.map(month => (
                            <option key={month.value} value={month.value}>
                                {month.label}
                            </option>
                        ))}
                    </select>
                </div>
                <div style={{ gridColumn: '1 / span 2' }}>
                    <label>Address / Location Details</label>
                    <textarea 
                        style={{ ...styles.input, minHeight: '80px' }} 
                        name="address"
                        value={settings.address}
                        onChange={handleInputChange}
                        placeholder="Physical Address"
                    />
                </div>
            </div>

            <button 
                style={{ ...styles.button, ...styles.success, marginTop: '20px' }}
                onClick={handleSave}
            >
                Save Settings
            </button>
        </div>
    );
};


// --------------------------------------------------
// PLACEHOLDER FOR OTHER MANAGERS (keep your original code)
// --------------------------------------------------
const MonthlyInvoicesManager = ({ reportStatusSetter }) => { /* ... */ };
const BankStatementUpload = ({ reportStatusSetter }) => { /* ... */ };
const BankReconciliationManager = ({ reportStatusSetter }) => { /* ... */ };

// --------------------------------------------------
// MAIN COMPONENT
// --------------------------------------------------
function CompanyCenter() {
    const [reportStatus, setReportStatus] = useState(null);

    const renderStatus = (status) => {
        if (!status) return null;
        const s = status.type === 'success'
            ? { backgroundColor: '#d4edda', color: '#155724' }
            : { backgroundColor: '#f8d7da', color: '#721c24' };
        return <div style={{ ...styles.status, ...s }}>{status.message}</div>;
    };

    return (
        <div style={styles.container}>
            <h2 style={styles.header}>Company Center (3.5)</h2>

            <SettingsManager reportStatusSetter={setReportStatus} />
	    <EmployeesManager reportStatusSetter={setReportStatus} />
            <BlocksManager reportStatusSetter={setReportStatus} />

            <GalleryManager reportStatusSetter={setReportStatus} />
	    <MonthlyInvoicesManager reportStatusSetter={setReportStatus} />
            <BankStatementUpload reportStatusSetter={setReportStatus} />
            <BankReconciliationManager reportStatusSetter={setReportStatus} />

            

            {renderStatus(reportStatus)}
        </div>
    );
}

export default CompanyCenter;